import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, RotateCcw, Save, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TAGS = ["first_name", "name", "service", "date", "time", "end_time", "hours", "studio_phone"];
const FIELDS = [
  { key: "subject", label: "Subject line", rows: 0 },
  { key: "greeting", label: "Greeting", hint: "First line is shown bold; each new line is its own paragraph.", rows: 3 },
  { key: "notes", label: "Extra notes", hint: "Optional — shown under the receipt (parking, what to bring, etc.).", rows: 4 },
];

const usePreview = (vals) => {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!vals) return;
    const t = setTimeout(() => api.post("/admin/receipt-email/preview", vals).then((r) => setPreview(r.data)).catch(() => {}), 400);
    return () => clearTimeout(t);
  }, [vals]);
  return preview;
};

export const ReceiptEmailCard = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["receipt-email"], queryFn: () => api.get("/admin/receipt-email").then((r) => r.data) });
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data && !vals) setVals(data.template); }, [data, vals]);
  const preview = usePreview(vals);
  if (!vals) return null;
  const dirty = FIELDS.some((f) => vals[f.key] !== data.template[f.key]);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/receipt-email", vals); setVals(r.data.template); qc.setQueryData(["receipt-email"], { ...data, template: r.data.template }); toast.success("Receipt email saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-5" data-testid="receipt-email-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h2 className="font-display text-lg text-white flex items-center gap-2"><Mail size={16} className="text-[#D4AF37]" /> Receipt Email</h2>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setVals(data.defaults)} data-testid="receipt-reset" className="text-xs text-white/50 hover:text-[#D4AF37] flex items-center gap-1"><RotateCcw size={12} /> Reset to default</button>
          <button onClick={save} disabled={!dirty || saving} data-testid="receipt-save-btn" className="btn-gold rounded-full px-4 py-2 text-xs font-mono-label uppercase flex items-center gap-1.5 disabled:opacity-40"><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
      <p className="text-white/40 text-xs mb-3">Emailed right after a client pays. Booking details, total, door code and address are filled in automatically.</p>
      <p className="text-white/40 text-[11px] mb-3">Placeholders: {TAGS.map((t) => <code key={t} className="text-[#D4AF37] mr-1.5">{`{${t}}`}</code>)}</p>
      <div className="space-y-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-white/60 text-xs">{f.label}</label>
            {f.rows ? <Textarea rows={f.rows} value={vals[f.key]} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="bg-[#0a0a0a] border-white/15 text-sm mt-1" data-testid={`receipt-input-${f.key}`} />
              : <Input value={vals[f.key]} maxLength={150} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="bg-[#0a0a0a] border-white/15 text-sm mt-1" data-testid={`receipt-input-${f.key}`} />}
            {f.hint && <p className="text-white/30 text-[11px] mt-1">{f.hint}</p>}
          </div>
        ))}
      </div>
      {preview?.warning && <p className="text-orange-400 text-xs mt-3 flex items-center gap-1.5" data-testid="receipt-warning"><AlertTriangle size={13} /> {preview.warning}</p>}
      <div className="mt-4 rounded-xl border border-white/5 overflow-hidden">
        <p className="text-[10px] uppercase font-mono-label text-white/40 px-3 py-2 bg-[#0a0a0a]">Preview · Subject: <span className="text-white/80 normal-case" data-testid="receipt-preview-subject">{preview?.subject}</span></p>
        <iframe title="Receipt preview" srcDoc={preview?.html || ""} sandbox="allow-same-origin" className="w-full h-[620px] bg-[#050505]" data-testid="receipt-preview-frame" />
      </div>
    </div>
  );
};
